import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import { fmtNum, fmtDate } from "@/lib/format";
import { Calculator } from "lucide-react";

export default function AssessmentsSection({ app, assessments, reload }) {
  return (
    <div className="space-y-3">
      <SectionHeader title="WC / TL / CGTMSE Assessments" subtitle="Run lender-side calculators with sensitivity." />
      <Tabs defaultValue="wc">
        <TabsList className="bg-muted/60">
          <TabsTrigger value="wc" data-testid="asmt-tab-wc">Working Capital</TabsTrigger>
          <TabsTrigger value="tl" data-testid="asmt-tab-tl">Term Loan</TabsTrigger>
          <TabsTrigger value="cgtmse" data-testid="asmt-tab-cgtmse">CGTMSE</TabsTrigger>
        </TabsList>
        <TabsContent value="wc"><WC app={app} reload={reload} assessments={assessments.filter(a => a.kind === "wc")} /></TabsContent>
        <TabsContent value="tl"><TL app={app} reload={reload} assessments={assessments.filter(a => a.kind === "tl")} /></TabsContent>
        <TabsContent value="cgtmse"><CGTMSE app={app} reload={reload} assessments={assessments.filter(a => a.kind === "cgtmse")} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>{children}</div>);
}

function WC({ app, reload, assessments }) {
  const [method, setMethod] = useState("nayak");
  const [t, setT] = useState(540);
  const [margin, setMargin] = useState(25);
  const [bankMargin, setBankMargin] = useState(5);
  const [invDays, setInvDays] = useState(60);
  const [recDays, setRecDays] = useState(45);
  const [payDays, setPayDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const payload = { application_id: app.id, method, projected_turnover_lakhs: Number(t), margin_percent: Number(margin), bank_margin_percent: Number(bankMargin) };
      if (method === "abf") Object.assign(payload, { inventory_days: Number(invDays), receivable_days: Number(recDays), payable_days: Number(payDays) });
      const res = await api.post("/assessments/wc", payload);
      setResult(res.data.result);
      toast.success("WC assessed");
      reload && reload();
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 mt-3">
      <Card className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <Row label="Method">
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="wc-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="nayak">Nayak Committee (25% of T/O)</SelectItem>
              <SelectItem value="traditional">Traditional (WC Gap)</SelectItem>
              <SelectItem value="abf">Assessed Bank Finance (Cycle)</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        <Row label="Projected Turnover (₹ L)"><Input type="number" value={t} onChange={(e) => setT(e.target.value)} data-testid="wc-turnover-input" /></Row>
        <Row label="Margin %"><Input type="number" value={margin} onChange={(e) => setMargin(e.target.value)} /></Row>
        <Row label="Bank Margin (Nayak) %"><Input type="number" value={bankMargin} onChange={(e) => setBankMargin(e.target.value)} /></Row>
        {method === "abf" && <>
          <Row label="Inventory Days"><Input type="number" value={invDays} onChange={(e) => setInvDays(e.target.value)} /></Row>
          <Row label="Receivable Days"><Input type="number" value={recDays} onChange={(e) => setRecDays(e.target.value)} /></Row>
          <Row label="Payable Days"><Input type="number" value={payDays} onChange={(e) => setPayDays(e.target.value)} /></Row>
        </>}
        <div className="md:col-span-4 flex justify-end"><Button onClick={run} disabled={busy} data-testid="wc-run-button"><Calculator className="h-4 w-4 mr-1" />Compute WC</Button></div>
      </Card>
      {result && (
        <Card className="p-4">
          <SectionHeader title="Recommended Limit" subtitle={`₹ ${fmtNum(result.recommended_limit)} Lakhs`} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(result.breakdown).map(([k, v]) => (
              <div key={k} className="rounded-md border p-3"><div className="text-xs text-muted-foreground">{k}</div><div className="text-base font-semibold mono">{typeof v === "number" ? fmtNum(v) : v}</div></div>
            ))}
          </div>
        </Card>
      )}
      <HistoryList items={assessments} renderItem={(a) => (
        <span><Badge variant="secondary">{a.input?.method}</Badge> T/O ₹{fmtNum(a.input?.projected_turnover_lakhs)}L → MPBF ₹{fmtNum(a.result?.recommended_limit)}L</span>
      )} />
    </div>
  );
}

function TL({ app, reload, assessments }) {
  const [form, setForm] = useState({ project_cost_lakhs: 100, debt_equity: 1.5, tenor_months: 60, moratorium_months: 6, interest_rate_pa: 11.5, expected_annual_revenue_lakhs: 540, expected_annual_profit_lakhs: 40, fixed_costs_lakhs: 70, variable_costs_lakhs: 400 });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const run = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, k === "tenor_months" || k === "moratorium_months" ? parseInt(v) : Number(v)]));
      payload.application_id = app.id;
      const res = await api.post("/assessments/tl", payload);
      setResult(res.data.result);
      toast.success("TL assessed");
      reload && reload();
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 mt-3">
      <Card className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Row label="Project Cost (₹ L)"><Input type="number" value={form.project_cost_lakhs} onChange={(e) => set("project_cost_lakhs", e.target.value)} /></Row>
        <Row label="Debt:Equity"><Input type="number" step="0.1" value={form.debt_equity} onChange={(e) => set("debt_equity", e.target.value)} /></Row>
        <Row label="Tenor (months)"><Input type="number" value={form.tenor_months} onChange={(e) => set("tenor_months", e.target.value)} /></Row>
        <Row label="Moratorium (months)"><Input type="number" value={form.moratorium_months} onChange={(e) => set("moratorium_months", e.target.value)} /></Row>
        <Row label="Interest Rate (% p.a.)"><Input type="number" step="0.05" value={form.interest_rate_pa} onChange={(e) => set("interest_rate_pa", e.target.value)} /></Row>
        <Row label="Expected Annual Revenue (₹ L)"><Input type="number" value={form.expected_annual_revenue_lakhs} onChange={(e) => set("expected_annual_revenue_lakhs", e.target.value)} /></Row>
        <Row label="Expected Annual Profit (₹ L)"><Input type="number" value={form.expected_annual_profit_lakhs} onChange={(e) => set("expected_annual_profit_lakhs", e.target.value)} /></Row>
        <Row label="Fixed Costs (₹ L)"><Input type="number" value={form.fixed_costs_lakhs} onChange={(e) => set("fixed_costs_lakhs", e.target.value)} /></Row>
        <Row label="Variable Costs (₹ L)"><Input type="number" value={form.variable_costs_lakhs} onChange={(e) => set("variable_costs_lakhs", e.target.value)} /></Row>
        <div className="md:col-span-3 flex justify-end"><Button onClick={run} disabled={busy} data-testid="tl-run-button"><Calculator className="h-4 w-4 mr-1" />Compute TL</Button></div>
      </Card>
      {result && (
        <Card className="p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat k="Loan Amount" v={`₹ ${fmtNum(result.loan_amount)} L`} />
            <Stat k="Promoter" v={`₹ ${fmtNum(result.promoter_contribution)} L`} />
            <Stat k="Monthly EMI" v={`₹ ${fmtNum(result.monthly_emi)} L`} />
            <Stat k="DSCR Year-1" v={fmtNum(result.dscr_year1, 2)} />
            <Stat k="BEP Revenue" v={result.bep_revenue_lakhs ? `₹ ${fmtNum(result.bep_revenue_lakhs)} L` : "—"} />
            <Stat k="Contribution Margin" v={fmtNum(result.contribution_margin_ratio * 100, 2) + "%"} />
          </div>
          <div>
            <h4 className="text-sm font-semibold heading mb-2">Sensitivity Analysis (DSCR)</h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              <Mini k="Baseline" v={fmtNum(result.sensitivity?.baseline_dscr, 2)} />
              <Mini k="Revenue -10%" v={fmtNum(result.sensitivity?.revenue_minus_10pct_dscr, 2)} />
              <Mini k="Revenue +10%" v={fmtNum(result.sensitivity?.revenue_plus_10pct_dscr, 2)} />
              <Mini k="Interest +2%" v={fmtNum(result.sensitivity?.interest_plus_2pct_dscr, 2)} />
              <Mini k="Interest -2%" v={fmtNum(result.sensitivity?.interest_minus_2pct_dscr, 2)} />
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold heading mb-2">Repayment Schedule (first 24 months)</h4>
            <div className="overflow-x-auto max-h-64 border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-muted/60 text-muted-foreground"><tr><th className="text-left px-2 py-1">Mo</th><th className="text-right px-2 py-1">EMI</th><th className="text-right px-2 py-1">Principal</th><th className="text-right px-2 py-1">Interest</th><th className="text-right px-2 py-1">Balance</th></tr></thead>
                <tbody>{result.schedule_first_24?.map((row) => (<tr key={row.month} className="border-t border-border"><td className="px-2 py-1 mono">{row.month}</td><td className="px-2 py-1 num-right">{fmtNum(row.emi)}</td><td className="px-2 py-1 num-right">{fmtNum(row.principal)}</td><td className="px-2 py-1 num-right">{fmtNum(row.interest)}</td><td className="px-2 py-1 num-right">{fmtNum(row.balance)}</td></tr>))}</tbody>
              </table>
            </div>
          </div>
        </Card>
      )}
      <HistoryList items={assessments} renderItem={(a) => (<span>Loan ₹{fmtNum(a.result?.loan_amount)}L → EMI ₹{fmtNum(a.result?.monthly_emi)} · DSCR {fmtNum(a.result?.dscr_year1, 2)}</span>)} />
    </div>
  );
}

function CGTMSE({ app, reload, assessments }) {
  const [form, setForm] = useState({ loan_amount_lakhs: 50, enterprise_category: "micro", hybrid_model: true, collateral_value_lakhs: 30, activity_type: "trading", woman_entrepreneur: false, ne_or_jk: true });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const run = async () => {
    setBusy(true);
    try {
      const payload = { ...form, application_id: app.id, loan_amount_lakhs: Number(form.loan_amount_lakhs), collateral_value_lakhs: Number(form.collateral_value_lakhs) || 0 };
      const res = await api.post("/assessments/cgtmse", payload);
      setResult(res.data.result);
      toast.success("CGTMSE assessed");
      reload && reload();
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 mt-3">
      <Card className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Row label="Loan Amount (₹ L)"><Input type="number" value={form.loan_amount_lakhs} onChange={(e) => set("loan_amount_lakhs", e.target.value)} data-testid="cgtmse-amount" /></Row>
        <Row label="Enterprise Category">
          <Select value={form.enterprise_category} onValueChange={(v) => set("enterprise_category", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["micro", "small", "medium"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
        <Row label="Activity Type">
          <Select value={form.activity_type} onValueChange={(v) => set("activity_type", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["trading", "manufacturing", "service"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
        <Row label="Collateral Value (₹ L)"><Input type="number" value={form.collateral_value_lakhs} onChange={(e) => set("collateral_value_lakhs", e.target.value)} /></Row>
        <SwitchRow label="Hybrid Model" checked={form.hybrid_model} onChange={(v) => set("hybrid_model", v)} />
        <SwitchRow label="NE / J&K Region" checked={form.ne_or_jk} onChange={(v) => set("ne_or_jk", v)} />
        <SwitchRow label="Woman Entrepreneur" checked={form.woman_entrepreneur} onChange={(v) => set("woman_entrepreneur", v)} />
        <div className="md:col-span-3 flex justify-end"><Button onClick={run} disabled={busy} data-testid="cgtmse-run-button"><Calculator className="h-4 w-4 mr-1" />Check Eligibility</Button></div>
      </Card>
      {result && (
        <Card className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat k="Eligible" v={result.eligible ? "Yes" : "No"} />
            <Stat k="Coverage %" v={`${result.coverage_percentage}%`} />
            <Stat k="Coverage Amount" v={`₹ ${fmtNum(result.coverage_amount_lakhs)} L`} />
            <Stat k="Annual Fee" v={`₹ ${fmtNum(result.annual_guarantee_fee_lakhs)} L (${result.annual_guarantee_fee_pct}%)`} />
          </div>
          {(result.reasons || []).filter(Boolean).length > 0 && (
            <div className="mt-3 text-sm text-warning"><b>Notes:</b><ul className="list-disc list-inside">{result.reasons.filter(Boolean).map((r, i) => <li key={i}>{r}</li>)}</ul></div>
          )}
          {(result.notes || []).filter(Boolean).length > 0 && (
            <div className="mt-2 text-xs text-muted-foreground"><ul className="list-disc list-inside">{result.notes.filter(Boolean).map((r, i) => <li key={i}>{r}</li>)}</ul></div>
          )}
        </Card>
      )}
      <HistoryList items={assessments} renderItem={(a) => (<span>Loan ₹{fmtNum(a.input?.loan_amount_lakhs)}L · Coverage {a.result?.coverage_percentage}% · Fee ₹{fmtNum(a.result?.annual_guarantee_fee_lakhs)}L</span>)} />
    </div>
  );
}

function SwitchRow({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function Stat({ k, v }) {
  return (<div className="rounded-md border p-3 bg-muted/30"><div className="text-[11px] uppercase text-muted-foreground font-medium">{k}</div><div className="mt-0.5 text-base font-semibold mono">{v}</div></div>);
}
function Mini({ k, v }) { return (<div className="rounded-md border p-2"><div className="text-[10px] uppercase text-muted-foreground">{k}</div><div className="text-sm font-semibold mono">{v}</div></div>); }
function HistoryList({ items, renderItem }) {
  if (!items?.length) return null;
  return (
    <Card className="p-3">
      <div className="text-xs text-muted-foreground mb-1">Recent runs</div>
      <ul className="divide-y divide-border">
        {items.map((a) => (
          <li key={a.id} className="py-2 text-sm flex items-center justify-between">
            <div>{renderItem(a)}</div>
            <div className="text-xs text-muted-foreground mono">{fmtDate(a.created_at)}</div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
